import type { RunnerResponse } from './evaluator-types.ts';

/*
 * The client's API-call kit (plans/surface-api-errors.md). The rules it
 * exists to enforce, in one line each:
 *
 *   - No silent drops: a failed call is always visible somewhere on the page.
 *   - No success UI without a 2xx: a notice may say "Saved"/"Done" only from
 *     a path that saw `r.ok`.
 *   - Every async event handler is wrapped (`reportErrors`) so a rejection
 *     lands in `pageError` instead of the console.
 *   - Every entry point's bootstrap ends in `.catch(bootstrapFailed)`.
 *
 * `ApiError` is the one error shape: `status` is the HTTP status (null for a
 * network failure) and `detail` the server's `{error}` body when it sent
 * one. `describe(e, doing)` renders any thrown thing — ApiError, a fetch
 * TypeError, a storage.ts status-bearing Error — as "Couldn't <doing>:
 * <detail> (<status>)", so a teacher's screen shows the server's own
 * diagnosis ("Bad quiz name: …") rather than a bare status code.
 *
 * Bare `fetch(` is allowed only in this file and storage.ts (which has its
 * own retry/report stack); scripts/check-client-fetch enforces that. Call
 * sites use `apiFetch` (a fetch whose network failure is an ApiError; the
 * non-2xx Response comes back for callers that interpret 401/403/404),
 * `fetchOk` (throws on anything but 2xx), or the `getJson`/`getText`/
 * `jsonIfOk`/`textIfOk` sugar.
 */

export class ApiError extends Error {
  status: number | null; // null: the request never got a response
  statusText: string | null;
  detail: string | null; // the body's `error` (or `message`) field, if any
  url: string | null;

  constructor(
    status: number | null,
    statusText: string | null,
    detail: string | null,
    url: string | null = null,
  ) {
    super(
      status === null
        ? `network failure${url ? ` (${url})` : ''}`
        : `HTTP ${status}${detail ? `: ${detail}` : statusText ? ` ${statusText}` : ''}`,
    );
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.detail = detail;
    this.url = url;
  }
}

/*
 * Build an ApiError from a non-2xx Response, pulling the server's `error`
 * field out of a JSON body when there is one. Consumes the body; never
 * throws itself (a body that won't parse just leaves detail null).
 */
export const apiError = async (r: Response): Promise<ApiError> => {
  let detail: string | null = null;
  try {
    const text = await r.text();
    try {
      const body = JSON.parse(text);
      if (body && typeof body === 'object') {
        const d = body.error ?? body.message;
        if (typeof d === 'string' && d.trim()) detail = d.trim();
      }
    } catch {
      // Not JSON (an HTML error page, say): no detail to show.
    }
  } catch {
    // Body unreadable: same.
  }
  return new ApiError(r.status, r.statusText || null, detail, r.url || null);
};

// fetch() rejects with a TypeError when the request never got a response
// (offline, DNS, CORS, the server mid-redeploy).
const isNetworkFailure = (e: unknown): boolean =>
  e instanceof TypeError || (e instanceof Error && e.name === 'TypeError');

const asApiError = (e: unknown, url: string): ApiError =>
  e instanceof ApiError
    ? e
    : isNetworkFailure(e)
      ? new ApiError(null, null, null, url)
      : new ApiError(null, null, e instanceof Error ? e.message : String(e), url);

/*
 * The "what went wrong" half of an error message: the server's detail when it
 * sent one, else the status text, else "server unreachable"; with the status
 * code when there is one. Also understands storage.ts's `{status}` Errors
 * and a raw thrown Response (the pre-kit convention).
 */
export const errorDetail = (e: unknown): string => {
  if (e instanceof ApiError) {
    if (e.status === null) return e.detail ?? 'server unreachable';
    return `${e.detail ?? e.statusText ?? 'request failed'} (${e.status})`;
  }
  if (typeof Response !== 'undefined' && e instanceof Response) {
    return `${e.statusText || 'request failed'} (${e.status})`;
  }
  if (isNetworkFailure(e)) return 'server unreachable';
  if (e instanceof Error) {
    const status = (e as { status?: unknown }).status;
    return typeof status === 'number' ? `${e.message} (${status})` : e.message;
  }
  return String(e);
};

// The standard one-line failure message: "Couldn't save your answer: Bad
// quiz name: u1l3.5 (400)".
export const describe = (e: unknown, doing: string): string =>
  `Couldn't ${doing}: ${errorDetail(e)}`;

/*
 * fetch whose network failure is an ApiError (status null) rather than a
 * bare TypeError. Non-2xx responses come back as-is for callers that
 * interpret them (401 → logged out, 404 → nothing there yet, …); callers
 * that don't chain `jsonIfOk`/`textIfOk`/`isOk` or use `fetchOk`.
 */
export const apiFetch = async (url: string | URL, init?: RequestInit): Promise<Response> => {
  try {
    return await fetch(url, init);
  } catch (e) {
    throw asApiError(e, String(url));
  }
};

// fetch that throws an ApiError for anything but a 2xx.
export const fetchOk = async (url: string | URL, init?: RequestInit): Promise<Response> => {
  const r = await apiFetch(url, init);
  if (!r.ok) throw await apiError(r);
  return r;
};

export const getJson = <T = unknown>(url: string | URL, init?: RequestInit): Promise<T> =>
  fetchOk(url, init).then((r) => r.json() as Promise<T>);

export const getText = (url: string | URL, init?: RequestInit): Promise<string> =>
  fetchOk(url, init).then((r) => r.text());

const jsonIfOk = async <T = unknown>(r: Response): Promise<T> => {
  if (r.ok) {
    return r.json() as Promise<T>;
  }
  throw await apiError(r);
};

const textIfOk = async (r: Response): Promise<string> => {
  if (r.ok) {
    return r.text();
  }
  throw await apiError(r);
};

const isOk = async (r: Response): Promise<true> => {
  if (r.ok) {
    return true;
  }
  throw await apiError(r);
};

/*
 * Send `data` as a JSON request body with the given method, returning the
 * Response promise for the caller to consume (non-2xx included; a network
 * failure rejects with an ApiError).
 */
const jsonRequest =
  (method: string) =>
  (url: string, data: unknown): Promise<Response> =>
    apiFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

const postJson = jsonRequest('POST');
const patchJson = jsonRequest('PATCH');
const putJson = jsonRequest('PUT');

/*
 * POST a JSON body to a runner-backed endpoint, retrying 503s and 502s. A
 * 503 from the runner means the machine pool is saturated (see the
 * repo-root DEPLOY.md, "Scaling the runner"); a 502 means the website's
 * submission proxy couldn't get an answer out of the runner at all —
 * typically a runner machine fly stopped (autostop, a deploy) under the
 * request, which is back serving a few seconds later (the 2026-10-07
 * "Unexpected status 502" incident: six gradings dropped by one autostop,
 * each a hard failure on the student's screen with the machine up again
 * 3s on). Nothing else retries — not the server submission proxy, not
 * fly-proxy (a POST is never replayed) — so either status lands straight
 * on the student. Retry with jittered exponential backoff to ride out a
 * class-sized burst or a machine restart; the jitter keeps a whole period
 * that hit 503 in lockstep from re-saturating the pool in lockstep. On
 * the terminal attempt the payload is returned so each caller's existing
 * 503 branch shows the overloaded message (and a 502 falls to its
 * unexpected-status branch). Total added wait is ~1+2+4+8s across
 * MAX_RETRIES. `message` reports the between-attempt waits to the student.
 * A network failure rejects with an ApiError, which the evaluators render
 * through `describe`.
 */
const MAX_RETRIES = 4;
const RETRY_BASE_MS = 1000;

const postJsonWithRetry = async (
  url: string,
  body: string,
  message: (text: string) => void,
): Promise<RunnerResponse> => {
  const headers = { 'Content-Type': 'application/json' };
  for (let attempt = 0; ; attempt++) {
    const r = await apiFetch(url, { method: 'POST', headers, body });
    let payload: RunnerResponse;
    try {
      payload = await r.json();
    } catch {
      payload = { status: r.status };
    }
    const status = payload?.status ?? r.status;
    const overloaded = r.status === 503 || status === 503;
    const unreachable = r.status === 502 || status === 502;
    if (!(overloaded || unreachable) || attempt >= MAX_RETRIES) return payload;
    const backoff = RETRY_BASE_MS * 2 ** attempt;
    const delay = Math.round(backoff * (0.5 + Math.random())); // full-ish jitter
    const why = overloaded ? 'Server busy' : 'Test server restarting';
    message(`${why} — retrying in ${Math.round(delay / 1000)}s …`);
    await new Promise((res) => setTimeout(res, delay));
  }
};

////////////////////////////////////////////////////////////////////////////////
// The visible channels.

const STYLE_ID = 'api-error-kit-style';

// Styled from here rather than a stylesheet because the kit runs on pages
// with no common stylesheet (the BJC and BHSawesome overlays, the gradebook).
const KIT_CSS = `
#page-error {
  position: fixed; top: 0; left: 0; right: 0; z-index: 10000;
  display: flex; align-items: flex-start; gap: 1em;
  padding: 0.6em 1em; background: #b00020; color: #fff;
  font: 15px/1.4 system-ui, sans-serif; box-shadow: 0 2px 6px rgba(0,0,0,0.3);
}
#page-error .page-error-messages { flex: 1 1 auto; }
#page-error .page-error-messages p { margin: 0; padding: 0; }
#page-error .page-error-messages p + p { margin-top: 0.3em; }
#page-error button {
  flex: 0 0 auto; background: transparent; color: #fff; border: 1px solid #fff;
  border-radius: 3px; padding: 0 0.5em; font: inherit; cursor: pointer;
}
.sticky-error:not([hidden]) {
  display: inline-flex; align-items: center; gap: 0.5em;
  color: #b00020; font-weight: bold; font-style: normal;
}
.sticky-error button {
  background: transparent; border: 1px solid #b00020; color: #b00020;
  border-radius: 3px; padding: 0 0.4em; font: inherit; cursor: pointer;
}
`;

const ensureKitStyle = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = KIT_CSS;
  document.head.append(style);
};

/*
 * The last-resort visible channel: a dismissible fixed bar at the top of
 * the page. Deliberately unpolished — its job is to make "we would have
 * said nothing" impossible, not to be anyone's primary UX. Pages with a
 * real status element use that and reserve this for failures with no
 * natural home (bootstraps, handlers with no status line). Repeated calls
 * stack their messages (a duplicate is shown once).
 */
export const pageError = (message: string): void => {
  console.error(message);
  if (typeof document === 'undefined') return;
  ensureKitStyle();
  let bar = document.getElementById('page-error');
  if (!bar) {
    bar = document.createElement('div');
    bar.id = 'page-error';
    bar.setAttribute('role', 'alert');
    const messages = document.createElement('div');
    messages.className = 'page-error-messages';
    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.textContent = '✕';
    dismiss.title = 'Dismiss';
    dismiss.onclick = () => {
      bar?.remove();
    };
    bar.append(messages, dismiss);
    document.body.append(bar);
  }
  const messages = bar.querySelector('.page-error-messages') as HTMLElement;
  for (const p of messages.children) {
    if (p.textContent === message) return;
  }
  const p = document.createElement('p');
  p.textContent = message;
  messages.append(p);
};

/*
 * Wrap an async event handler so a rejection is reported (via `pageError`)
 * instead of becoming an unhandled rejection nobody sees. `doing` is the
 * verb phrase for the message: "Couldn't <doing>: …".
 */
export const reportErrors =
  <A extends unknown[]>(doing: string, fn: (...args: A) => unknown): ((...args: A) => void) =>
  (...args: A) => {
    void (async () => {
      try {
        await fn(...args);
      } catch (e) {
        console.error(e);
        pageError(describe(e, doing));
      }
    })();
  };

/*
 * Bootstrap guard: every entry point's trailing `setup()` becomes
 * `setup().catch(bootstrapFailed)`, so a page either works or says it
 * doesn't. `disable` lets pages where a half-alive editor is worse than a
 * dead one (Snap!, the legacy quiz) mark their input surface inert.
 */
export const bootstrapFailed = (e: unknown, disable?: () => void): void => {
  console.error('Page setup failed:', e);
  try {
    disable?.();
  } catch (disableError) {
    console.error(disableError);
  }
  pageError(`This page failed to load (${errorDetail(e)}). Reload to try again.`);
};

/*
 * A sticky per-page mutation-error line, separate from any polled status
 * element so the next "Updated <time>" can't clobber it. Created (hidden)
 * right after `anchor`; `show` reveals it with a message, `clear` hides it
 * (the next successful mutation clears; so does the dismiss button).
 */
export type StickyError = { show: (message: string) => void; clear: () => void };

export const stickyError = (anchor: HTMLElement): StickyError => {
  ensureKitStyle();
  const el = document.createElement('span');
  el.className = 'sticky-error';
  el.setAttribute('role', 'alert');
  el.hidden = true;
  const text = document.createElement('span');
  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.textContent = '✕';
  dismiss.title = 'Dismiss';
  el.append(text, dismiss);
  anchor.after(el);
  const clear = () => {
    el.hidden = true;
    text.textContent = '';
  };
  dismiss.onclick = clear;
  return {
    show(message) {
      console.error(message);
      text.textContent = message;
      el.hidden = false;
    },
    clear,
  };
};

/*
 * The admin/section pages' mutate-then-reload helper: fire a JSON request,
 * and on any failure say so (alert) without reloading, so a failed action
 * never looks like a successful one. Logged out (401 — e.g. the session
 * didn't survive a redeploy) goes to login and back here. Resolves with the
 * parsed body on success (after running `onOk`, default: reload) and null
 * on failure.
 */
export const sendAndReload = async (
  method: string,
  url: string,
  body?: unknown,
  onOk: (data: Record<string, unknown>) => void = () => location.reload(),
): Promise<Record<string, unknown> | null> => {
  let r: Response;
  try {
    r = await apiFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    console.error(e);
    alert("Couldn't reach the server. Check your connection and try again.");
    return null;
  }
  if (r.status === 401) {
    location.assign(`/login?from=${encodeURIComponent(location.pathname)}`);
    return null;
  }
  const data: Record<string, unknown> = await r
    .clone()
    .json()
    .catch(() => ({}));
  if (!r.ok || typeof data.error === 'string') {
    alert(typeof data.error === 'string' ? data.error : errorDetail(await apiError(r)));
    return null;
  }
  onOk(data);
  return data;
};

export { isOk, jsonIfOk, patchJson, postJson, postJsonWithRetry, putJson, textIfOk };
